/**
 * Редактор звіту про внесок на підтримку працевлаштування осіб з інвалідністю
 * (ч.1208, J0500701 / F0500701). З ч.1215 звіт на екрані — це сам бланк, затверджений
 * наказом Мінфіну № 453 (`DisabilityContributionReportBlank`): реквізити, клітинки, підписи,
 * а поля, які заповнює бухгалтер (номер звіту, категорія платника, рядки розділів І–ІІІ), —
 * жовті клітинки прямо у формі, як у декларації з ПДВ. Над бланком — ознака платника,
 * під ним — розрахунок з обліку (по місяцях і по працівниках), щоб було видно, звідки число.
 * «перевірити» — арифметика і структура файла, «перерахувати з обліку» — заново з кадрів і
 * нарахувань. Подання — чернетка у Кабінеті ДПС.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  QUARTER_LABELS, REPORT_TYPE_LABELS,
  contributionAmount, disabilityContributionReports, normWorkplaces, shortfall,
  type DisabilityCalcEmployee, type DisabilityCategory, type DisabilityFields,
  type DisabilityReportCheck,
} from '@/api/disabilityContributionReports';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { fmtMoney } from './SingleTaxBlank';
import {
  DisabilityReportBlankSheets, liveBlank, type DisabilityBlankEditor,
} from './DisabilityContributionReportBlank';
import TchTable from '@/shared/components/TchTable';

const MONTHS = ['', 'січень', 'лютий', 'березень', 'квітень', 'травень', 'червень',
  'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'];
const KIND_LABELS: Record<string, string> = { MAIN: 'основне місце', INTERNAL: 'внутрішній сумісник', EXTERNAL: 'зовнішній сумісник' };

/** Перерахунок залежних рядків бланка після правки (норматив, ряд.5, внесок, розділ III). */
/** Номер Звіту за період: ціле 1…999 або null, якщо введено не число. */
export function parseReportNumber(raw: string): number | null {
  if (!/^\d{1,3}$/.test(raw.trim())) return null;
  const n = Number(raw.trim());
  return n >= 1 ? n : null;
}

export function recomputeDependent(
  values: Record<string, string>, changed: string, fields: DisabilityFields,
): Record<string, string> {
  const v = { ...values };
  const int = (c: string) => Math.trunc(Number(v[c] ?? 0)) || 0;
  if (changed === 'R011G3') v.R013G3 = String(normWorkplaces(int('R011G3'), fields.category === '093'));
  if (['R011G3', 'R013G3', 'R014G3', 'R0141G3'].includes(changed)) {
    v.R015G3 = String(shortfall(int('R013G3'), int('R014G3'), int('R0141G3')));
  }
  if (['R011G3', 'R013G3', 'R014G3', 'R0141G3', 'R015G3', 'R022G3'].includes(changed)) {
    v.R023G3 = contributionAmount(v.R022G3 ?? '0', int('R015G3'), fields.martial_law_half !== false);
  }
  if (changed === 'R031G3' || changed === 'R032G3') {
    const a = Math.round(Number(v.R031G3 ?? 0) * 100);
    const b = Math.round(Number(v.R032G3 ?? 0) * 100);
    delete v.R033G3; delete v.R034G3;
    if (b > a) v.R033G3 = ((b - a) / 100).toFixed(2);
    if (b < a) v.R034G3 = ((a - b) / 100).toFixed(2);
  }
  return v;
}

export default function DisabilityContributionReportEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [values, setValues] = useState<Record<string, string>>({});
  const [fields, setFields] = useState<DisabilityFields>({});
  // ч.1211: номер Звіту за період — править бухгалтер (подано з іншої програми; після відмови ДПС — наступний).
  const [docCnt, setDocCnt] = useState('1');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [check, setCheck] = useState<DisabilityReportCheck | null>(null);
  const [checking, setChecking] = useState(false);

  const docQ = useQuery({ queryKey: ['disability-report', id], queryFn: () => disabilityContributionReports.get(id!), enabled: !!id });
  const d = docQ.data;
  // Реквізити організації у бланку — ті самі, що підуть у файл для ДПС.
  const blankQ = useQuery({ queryKey: ['disability-report-blank', id], queryFn: () => disabilityContributionReports.blank(id!), enabled: !!id });
  const subQ = useQuery({ queryKey: ['disability-report-sub', id], queryFn: () => disabilityContributionReports.submission(id!), enabled: !!id, refetchInterval: 30_000 });
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgListQ.data?.items.find((o) => o.id === d?.organization_id);

  useEffect(() => {
    if (d) { setValues(d.values ?? {}); setFields(d.fields ?? {}); setDocCnt(String(d.doc_cnt ?? 1)); setDirty(false); }
  }, [d]);

  const subStatus = subQ.data?.status ?? null;
  // Відхилений ДПС звіт лишається редагованим: його виправляють і подають знову з наступним номером.
  const readOnly = !!subStatus && !['DRAFT', 'ERROR', 'REJECTED'].includes(subStatus);
  const wasSent = subStatus === 'REJECTED';
  const isPayer = !!fields.is_payer;
  const isCorrection = d?.report_type === 'CORRECTING';
  const computed = d?.calc?.computed;
  const onErr = (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? String(e));
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['disability-report', id] });
    qc.invalidateQueries({ queryKey: ['disability-report-blank', id] });
  };

  const save = useMutation({
    mutationFn: () => {
      const number = parseReportNumber(docCnt);
      if (number === null) throw new Error('Номер звіту за період — ціле число від 1 до 999');
      return disabilityContributionReports.update(id!, { values, fields, doc_cnt: number });
    },
    onSuccess: () => { setDirty(false); setError(null); setCheck(null); invalidate(); },
    onError: onErr,
  });
  const recalc = useMutation({
    mutationFn: () => disabilityContributionReports.recalc(id!),
    onSuccess: () => { setError(null); setCheck(null); invalidate(); },
    onError: onErr,
  });
  const remove = useMutation({
    mutationFn: () => disabilityContributionReports.remove(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['disability-reports'] }); navigate('/taxation/disability-contribution-reports'); },
    onError: onErr,
  });
  const saveDocDate = useMutation({
    mutationFn: (ymd: string) => disabilityContributionReports.update(id!, { document_date: `${ymd}T00:00:00Z` }),
    onSuccess: invalidate, onError: onErr,
  });

  async function runCheck() {
    if (!id) return;
    setChecking(true);
    try {
      if (dirty && !readOnly) await save.mutateAsync();
      setCheck(await disabilityContributionReports.check(id));
    } catch (e: any) { onErr(e); } finally { setChecking(false); }
  }
  async function openPrint() {
    if (dirty && !readOnly) await save.mutateAsync();
    navigate(`/taxation/disability-contribution-reports/${id}/print`);
  }
  const setV = (code: string, raw: string) => {
    setValues((prev) => {
      const next = { ...prev };
      if (raw === '') delete next[code]; else next[code] = raw;
      return recomputeDependent(next, code, fields);
    });
    setDirty(true);
  };
  const setCategory = (cat: DisabilityCategory) => {
    const nf = { ...fields, category: cat };
    setFields(nf);
    setValues((prev) => recomputeDependent(prev, 'R011G3', nf));
    setDirty(true);
  };
  const togglePayer = (on: boolean) => {
    if (on) {
      const nf: DisabilityFields = { ...fields, is_payer: true, category: fields.category ?? computed?.category ?? '091' };
      setFields(nf);
      // Рядки сіються з розрахунку з обліку (якщо бухгалтер ще нічого не вносив).
      setValues((prev) => (Object.keys(prev).some((k) => k.startsWith('R01') || k.startsWith('R02')) || !computed
        ? prev
        : { ...prev,
            R011G3: String(computed.R011G3), R012G3: String(computed.R012G3), R013G3: String(computed.R013G3),
            R014G3: String(computed.R014G3), R0141G3: String(computed.R0141G3), R015G3: String(computed.R015G3),
            R021G3: computed.R021G3, R022G3: computed.R022G3,
            R023G3: contributionAmount(computed.R022G3, computed.R015G3, fields.martial_law_half !== false) }));
    } else {
      setFields({ ...fields, is_payer: false, category: null });
    }
    setDirty(true);
  };

  const empCols = useMemo<ColumnDef<DisabilityCalcEmployee>[]>(() => {
    const months = (d?.calc?.months ?? []).map((m) => m.month);
    const sumOf = (r: Record<string, number>) => months.reduce((s, m) => s + (r?.[String(m)] ?? 0), 0);
    return [
      { key: 'name', header: 'Працівник', cell: (r) => r.name, sortValue: (r) => r.name },
      { key: 'kind', header: 'Зайнятість', width: 170,
        cell: (r) => r.employment_type === 'CIVIL_LAW' ? 'цивільно-правовий договір' : KIND_LABELS[r.kind] ?? r.kind },
      { key: 'base', header: 'Днів у чисельності', align: 'right', width: 130,
        cell: (r) => sumOf(r.base_days) || (r.hazardous ? 'шкідливі умови' : '—'), sortValue: (r) => sumOf(r.base_days) },
      { key: 'excl', header: 'Декрет / догляд, днів', align: 'right', width: 140,
        cell: (r) => sumOf(r.excluded_days) || '', sortValue: (r) => sumOf(r.excluded_days) },
      { key: 'absence', header: 'Відсутність за наказом', width: 240,
        cell: (r) => r.absence_note
          ? <span title="Військова служба і призупинений договір: у чисельності для нормативу лишається, у середню кількість для зарплати не входить">{r.absence_note}</span>
          : '',
        sortValue: (r) => r.absence_note ?? '' },
      { key: 'dis', header: 'Інвалідність', width: 190,
        cell: (r) => !r.disabled ? '' : (
          <span style={{ color: sumOf(r.norm_days) > 0 ? 'var(--badge-green-fg)' : 'var(--badge-amber-fg)' }}>
            {sumOf(r.norm_days) > 0 ? `зараховано, ${sumOf(r.norm_days)} дн.` : 'не зараховано'}
            {r.double_count ? ' · як 2 місця' : ''}
          </span>
        ), sortValue: (r) => (r.disabled ? 1 : 0) },
      { key: 'gross', header: 'Нараховано за квартал', align: 'right', width: 160,
        cell: (r) => r.employment_type === 'CIVIL_LAW' || r.hazardous ? 'не входить' : fmtMoney(r.gross_quarter),
        sortValue: (r) => Number(r.gross_quarter) },
    ];
  }, [d]);

  if (!d) return <div className="card">Завантаження…</div>;
  const title = `Звіт про внесок (особи з інвалідністю) — ${QUARTER_LABELS[d.period_quarter]} ${d.period_year}`;
  // Підказка під полем бланка: значення з обліку — коли воно щось каже (рядок не заповнено
  // або введене відрізняється). Збігається — підказки немає, бланк лишається чистим.
  const calcHint = (code: string) => {
    if (!computed || code.startsWith('R03')) return null;
    const c = (computed as unknown as Record<string, string | number>)[code];
    if (c == null) return null;
    const entered = isPayer ? values[code] : undefined;
    const differs = entered != null && entered !== '' && Number(entered) !== Number(c);
    if (entered != null && entered !== '' && !differs) return null;
    return { text: code.startsWith('R02') ? fmtMoney(String(c)) : String(c), differs };
  };
  const blankEditor: DisabilityBlankEditor = {
    readOnly, isPayer, isCorrection,
    values, setValue: setV,
    category: fields.category, setCategory,
    number: docCnt, setNumber: (raw) => { setDocCnt(raw); setDirty(true); },
    liquidatedTin: fields.liquidated_tin ?? '',
    setLiquidatedTin: (raw) => { setFields((prev) => ({ ...prev, liquidated_tin: raw })); setDirty(true); },
    hint: calcHint,
  };
  const blank = liveBlank(d, blankQ.data, {
    values, isPayer, category: fields.category, number: docCnt, liquidatedTin: fields.liquidated_tin ?? '',
  });
  const missing = blankQ.data?.missing ?? [];

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/taxation/disability-contribution-reports')}>до списку</button>
          {!readOnly && (
            <>
              <button className="primary" onClick={() => save.mutate()} disabled={save.isPending || !dirty}>зберегти</button>
              <button onClick={runCheck} disabled={checking}>{checking ? '…' : 'перевірити'}</button>
              <button disabled={recalc.isPending} onClick={() => {
                confirm('Перерахувати з обліку? Ручні правки рядків розділів І–ІІ буде перезаписано.')
                  .then((ok) => { if (ok) recalc.mutate(); });
              }}>перерахувати з обліку</button>
              <button className="danger" disabled={wasSent}
                title={wasSent ? 'Звіт уже надсилали до ДПС — видалити не можна: виправте його і подайте повторно з наступним номером' : undefined}
                onClick={() => { confirm('Видалити звіт?').then((ok) => { if (ok) remove.mutate(); }); }}>видалити</button>
            </>
          )}
          <button onClick={openPrint} title="Друкована форма звіту">друк</button>
        </div>
      </div>

      <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)', color: 'var(--badge-blue-fg)',
        padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          {subStatus === 'ACCEPTED' ? '✓ прийнято ДПС (квитанція №2)'
            : subStatus === 'REJECTED' ? '✗ відхилено ДПС — виправте звіт, вкажіть наступний номер звіту і подайте повторно'
            : subStatus === 'DELIVERED' ? '✓ доставлено (кв.1), очікуємо кв.2'
            : subStatus === 'SENT' ? '→ відправлено у ДПС'
            : subStatus === 'SIGNED' ? 'підписано — лишилося відправити у Кабінеті ДПС'
            : 'збережено чернеткою у Кабінеті ДПС — підпишіть і відправте там'}
        </span>
        <a style={{ cursor: 'pointer', marginLeft: 'auto' }} onClick={async () => {
          if (dirty && !readOnly) {
            try { await save.mutateAsync(); } catch { return; }
          }
          navigate('/dps-cabinet');
        }}>Кабінет ДПС →</a>
      </div>

      <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 8, display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>{d.document_number} · {d.form_code} · {REPORT_TYPE_LABELS[d.report_type]} · {org?.name ?? ''}</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Дата подання (у файлі та на бланку)">
          Дата подання
          <div style={{ width: 130 }}>
            <DatePopover value={d.document_date?.slice(0, 10) ?? ''} onChange={(v) => { if (v && !readOnly) saveDocDate.mutate(v); }} />
          </div>
        </label>
      </div>
      {error && <div style={{ color: 'var(--badge-red-fg)', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>}

      {check && (
        <div style={{ marginBottom: '0.75rem' }} data-testid="disability-check">
          {check.errors.length === 0 && (
            <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              ✓ Помилок не знайдено — звіт готовий до подання
            </div>
          )}
          {check.errors.length > 0 && (
            <div style={{ background: 'var(--badge-red-bg)', color: 'var(--badge-red-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              <b>Помилки ({check.errors.length}) — виправте перед поданням:</b>
              <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>{check.errors.map((e, i) => <li key={i} style={{ fontSize: '0.85rem' }}>{e.message}</li>)}</ul>
            </div>
          )}
          {check.warnings.length > 0 && (
            <div style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)', padding: '0.5rem 0.75rem', borderRadius: 6, marginTop: 6 }}>
              <b>Перевірте ({check.warnings.length}):</b>
              <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>{check.warnings.map((w, i) => <li key={i} style={{ fontSize: '0.85rem' }}>{w.message}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem', marginBottom: '0.75rem', background: 'var(--bg-soft)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600 }}>
          <input type="checkbox" checked={isPayer} disabled={readOnly} style={{ width: 'auto' }}
            onChange={(e) => togglePayer(e.target.checked)} />
          Платник внеску (норматив не виконано)
        </label>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 4 }}>
          {fields.below_threshold
            ? 'За розрахунком з обліку середньооблікова кількість менша за 8 працівників: норматив не встановлюється, звіт подається без розрахунку.'
            : fields.norm_met
              ? 'За розрахунком з обліку норматив виконано: внесок не нараховується, звіт подається без розрахунку.'
              : 'За розрахунком з обліку норматив не виконано: заповнюються категорія платника і розділи І–ІІ, внесок підлягає сплаті.'}
          {' '}У звіті роботодавця, який не є платником, рядки 091–093 і розділи І–ІІІ лишаються порожніми.
        </div>
        {isPayer && !readOnly && (
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 4 }}>
            Категорію платника позначте клацанням у рядках 091–093 бланка. Жовті клітинки — поля, які можна виправити.
          </div>
        )}
      </div>

      {missing.length > 0 && (
        <div style={{ background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)', color: 'var(--badge-amber-fg)',
          padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          У картці організації не заповнено: {missing.join('; ')}. У бланку ці поля порожні, файл для подання не сформується.{' '}
          <a style={{ cursor: 'pointer' }} onClick={() => navigate(`/organizations/${d.organization_id}`)}>Відкрити картку організації →</a>
        </div>
      )}

      {/* Звіт — у вигляді затвердженого бланка; поля вводу — жовті клітинки у формі (як декларація з ПДВ). */}
      <DisabilityReportBlankSheets blank={blank} editor={blankEditor} />

      <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: 8 }}>
        Внесок = 40 % × середньомісячна зарплата × 3 місяці × ряд.5 розділу І
        {fields.martial_law_half !== false ? ' × 50 % (на час воєнного стану)' : ''}.
        Сплата — протягом 10 календарних днів після граничного строку подання звіту.
        {isCorrection && d.corrected_period_year
          ? ` Розділ ІІІ уточнює ${QUARTER_LABELS[d.corrected_period_quarter ?? 1]} ${d.corrected_period_year}.` : ''}
      </div>

      {(d.calc?.months?.length ?? 0) > 0 && (
        <div style={{ marginTop: '1.2rem' }} data-testid="disability-calc">
          <h3 style={{ margin: '0 0 0.3rem', fontSize: '0.95rem' }}>Розрахунок з обліку</h3>
          {(d.calc.warnings ?? []).length > 0 && (
            <div style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: 6, fontSize: '0.85rem' }}>
              <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>{d.calc.warnings!.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
          <TchTable>
            <thead>
              <tr>
                <th>Місяць</th><th style={{ textAlign: 'right' }}>Календарних днів</th>
                <th style={{ textAlign: 'right' }}>Середньооблікова</th>
                <th style={{ textAlign: 'right' }}>З нарахованою зарплатою</th>
                <th style={{ textAlign: 'right' }}>Осіб з інвалідністю</th>
                <th style={{ textAlign: 'right' }}>з них як 2 місця</th>
                <th style={{ textAlign: 'right' }}>Мінімальна зарплата</th>
                <th>Нарахування</th>
              </tr>
            </thead>
            <tbody>
              {d.calc.months!.map((m) => (
                <tr key={m.month}>
                  <td>{MONTHS[m.month]}</td>
                  <td style={{ textAlign: 'right' }}>{m.calendar_days}</td>
                  <td style={{ textAlign: 'right' }}>{m.skshp} <span style={{ color: 'var(--text-secondary)' }}>({m.skshp_avg})</span></td>
                  <td style={{ textAlign: 'right' }}>{m.kser}</td>
                  <td style={{ textAlign: 'right' }}>{m.disabled} <span style={{ color: 'var(--text-secondary)' }}>({m.disabled_avg})</span></td>
                  <td style={{ textAlign: 'right' }}>{m.double}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoney(m.min_wage)}</td>
                  <td>{m.has_payroll ? 'проведено' : <span style={{ color: 'var(--badge-amber-fg)' }}>немає</span>}</td>
                </tr>
              ))}
            </tbody>
          </TchTable>
          {computed && (
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: '4px 0 8px' }}>
              Середня кількість працівників для зарплати: {computed.kser_staff} штатних + {computed.R012G3} сумісників
              ({computed.external_hours} год з {computed.norm_hours} год табельного фонду) = {computed.kser};
              місяців з нарахуванням: {computed.p_months}. Середньомісячна зарплата = {fmtMoney(computed.R021G3)} ÷ {computed.kser || '—'} ÷ {computed.p_months || '—'}.
            </div>
          )}
          <DataTable<DisabilityCalcEmployee>
            rows={d.calc.employees ?? []} columns={empCols} rowKey={(r) => r.employee_id}
            emptyText="У кварталі немає працівників з датою прийому" initialSortKey="name" />
        </div>
      )}
    </div>
  );
}
